import React from "react";
import { Link } from 'react-router-dom';

import SiteHeader, { WEBMAIL_URL } from '../SiteHeader';
import SiteFooter from '../SiteFooter';
import './Home.css';

const Check = () => (
	<svg className="dm-li-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5 9-10" /></svg>
);

const Info = () => (
	<svg className="dm-li-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" /></svg>
);

const features = [
	{
		title: 'Ready in a minute',
		text: 'Sign up and create your first address right away. No setup required.',
		icon: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
	},
	{
		title: 'Use your own domain',
		text: "Professional addresses like you@yourcompany.com. No domain yet? We'll help you register one.",
		icon: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" /></>,
	},
	{
		title: 'All mailboxes in one place',
		text: 'Create, disable and change passwords for every address from a single dashboard.',
		icon: <><rect x="3" y="4" width="18" height="6" rx="1.5" /><rect x="3" y="14" width="18" height="6" rx="1.5" /></>,
	},
	{
		title: 'Clean, modern webmail',
		text: 'Read and send email from any browser, in several languages.',
		icon: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></>,
	},
	{
		title: 'Works with your apps',
		text: 'Connect Outlook, Thunderbird or your phone through secure IMAP and SMTP.',
		icon: <><rect x="7" y="2" width="10" height="20" rx="2" /><path d="M11 18h2" /></>,
	},
	{
		title: 'Secure by default',
		text: 'Encrypted connections for webmail and email apps, with spam and virus filtering.',
		icon: <><path d="M12 3l8 3v6c0 4.5-3.4 8.2-8 9-4.6-.8-8-4.5-8-9V6z" /><path d="M9 12l2 2 4-4" /></>,
	},
];

const benefits = [
	{ title: 'Account recovery', text: 'Use it as a backup address to get back into your main account if you ever lose access.' },
	{ title: 'Privacy', text: 'Keep personal and work email apart. Share each address only with the right people.' },
	{ title: 'Less spam', text: 'Sign up for newsletters and online services with it, and keep your main inbox clean.' },
	{ title: 'Better organization', text: 'Use different addresses for different topics, so messages are easier to sort and find.' },
	{ title: 'Required by some services', text: 'Some platforms ask for a second email to finish registration or as a backup contact.' },
];

const plans = [
	{
		name: 'Free',
		tagline: 'Personal email @diamta.com',
		price: '$0',
		period: '/ year',
		items: [ 'Up to 5 addresses @diamta.com', '5 GB of storage in total', 'Send up to 50 emails per hour', 'Webmail and email app access' ],
		notes: [ 'Ad-supported webmail' ],
		cta: 'Create a free account',
	},
	{
		name: 'Business',
		tagline: 'Email on your own domain',
		price: '$15',
		period: '/ year per domain',
		items: [ 'Up to 10 mailboxes', '5 GB of storage per mailbox', 'Send up to 100 emails per hour per mailbox', 'No ads', 'Use your domain, or register one at cost' ],
		notes: [],
		cta: 'Get business email',
		featured: true,
		badge: 'Recommended',
	},
	{
		name: 'Business Plus',
		tagline: 'For growing teams',
		price: '$40',
		period: '/ year per domain',
		items: [ 'Up to 50 mailboxes', '10 GB of storage per mailbox', 'Send up to 200 emails per hour per mailbox', 'No ads', 'Domain registration at cost, plus setup help' ],
		notes: [],
		cta: 'Get Business Plus',
	},
];

const previewMailboxes = [
	{ email: 'jane@diamta.com', note: 'Personal' },
	{ email: 'info@yourcompany.com', note: 'Business' },
	{ email: 'sales@yourcompany.com', note: 'Business' },
];

// Illustration of the email manager, drawn in HTML/CSS so it stays sharp at any size
const HeroPreview = () => (
	<figure className="dm-hero-shot" role="img" aria-label="The Diamta Mail email manager, listing three mailboxes with buttons to open or edit each one">
		<div className="dm-pv" aria-hidden="true">
			<div className="dm-pv-bar">
				<span className="dm-pv-dot" /><span className="dm-pv-dot" /><span className="dm-pv-dot" />
				<span className="dm-pv-url">mail.diamta.com</span>
			</div>
			<div className="dm-pv-body">
				<div className="dm-pv-side">
					<span className="dm-pv-side-brand">
						<svg width="22" height="22" viewBox="0 0 40 40"><path d="M20 3 L37 20 L20 37 L3 20 Z" fill="none" stroke="currentColor" strokeWidth="3" strokeLinejoin="round" /><text x="20" y="25.5" textAnchor="middle" fontSize="15" fontWeight="800" fill="currentColor">@</text></svg>
						Diamta Mail
					</span>
					<span className="dm-pv-side-item is-active">Email manager</span>
					<span className="dm-pv-side-item">Add a domain</span>
					<span className="dm-pv-side-item">FAQ</span>
					<span className="dm-pv-side-item">Contact us</span>
				</div>
				<div className="dm-pv-main">
					<div className="dm-pv-top">
						<span className="dm-pv-search">
							<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="M20 20l-4-4" /></svg>
							Search an email
						</span>
						<span className="dm-pv-avatar">J</span>
					</div>
					<div className="dm-pv-head">
						<div>
							<span className="dm-pv-title">Email manager</span>
							<span className="dm-pv-sub">You have 3 email accounts</span>
						</div>
						<span className="dm-pv-new">+ New mailbox</span>
					</div>
					<div className="dm-pv-list">
						{previewMailboxes.map((m) => (
							<div className="dm-pv-row" key={m.email}>
								<span className="dm-pv-mail">
									<span className="dm-pv-icon">
										<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></svg>
									</span>
									<span className="dm-pv-mail-text">
										<b>{m.email}</b>
										<small>{m.note}</small>
									</span>
								</span>
								<span className="dm-pv-actions">
									<span className="dm-pv-btn dm-pv-btn-primary">Open mailbox</span>
									<span className="dm-pv-btn">Edit</span>
								</span>
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
		<div className="dm-pv-toast" aria-hidden="true">
			<span className="dm-pv-toast-icon">
				<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5 9-10" /></svg>
			</span>
			<span>
				<b>Mailbox created</b>
				<small>sales@yourcompany.com is ready</small>
			</span>
		</div>
	</figure>
);

const Home = () => {
	return (
		<div className="dm-site dm-home">
			<SiteHeader />

			<main>
				{/* Hero */}
				<section id="top" className="dm-hero">
					<div className="dm-container dm-hero-inner">
						<div className="dm-hero-copy">
							<span className="dm-pill">Free &amp; business email</span>
							<h1>Simple, secure email. Free to start.</h1>
							<p className="dm-lead">
								Create a free @diamta.com address in a minute, or run professional email on your own domain.
								Manage every mailbox from one place.
							</p>
							<div className="dm-actions">
								<Link to="/registration" className="dm-btn dm-btn-primary dm-btn-lg">Create a free account</Link>
								<a href={WEBMAIL_URL} className="dm-btn dm-btn-outline dm-btn-lg">Open webmail</a>
							</div>
							<p className="dm-muted dm-small">Already have an account? <Link to="/login"><b>Log in</b></Link></p>
						</div>
						<HeroPreview />
					</div>
				</section>

				{/* Features */}
				<section id="features" className="dm-section dm-container">
					<h2>Everything you need, nothing you don't</h2>
					<p className="dm-section-lead">One account to create, manage and read all your email addresses.</p>
					<div className="dm-grid dm-grid-features">
						{features.map((f) => (
							<div className="dm-card" key={f.title}>
								<svg className="dm-card-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{f.icon}</svg>
								<h3>{f.title}</h3>
								<p>{f.text}</p>
							</div>
						))}
					</div>
				</section>

				{/* Pricing */}
				<section id="pricing" className="dm-band">
					<div className="dm-section dm-container">
						<h2 className="dm-center">Simple pricing</h2>
						<p className="dm-section-lead dm-center">Start free. Upgrade when you want ad-free email on your own domain.</p>
						<div className="dm-plans">
							{plans.map( ( plan ) => (
								<div className={'dm-plan' + ( plan.featured ? ' dm-plan-featured' : '' )} key={plan.name}>
									{plan.badge && <span className="dm-pill dm-plan-badge">{plan.badge}</span>}
									<div>
										<h3>{plan.name}</h3>
										<p className="dm-muted">{plan.tagline}</p>
									</div>
									<p className="dm-price"><span>{plan.price}</span> {plan.period}</p>
									<ul>
										{plan.items.map( ( item ) => <li key={item}><Check />{item}</li> )}
										{plan.notes.map( ( note ) => <li className="dm-note" key={note}><Info />{note}</li> )}
									</ul>
									<Link to="/registration" className={'dm-btn dm-btn-lg ' + ( plan.featured ? 'dm-btn-primary' : 'dm-btn-outline-brand' )}>{plan.cta}</Link>
								</div>
							) )}
						</div>
					</div>
				</section>

				{/* Why a second address */}
				<section className="dm-section dm-container">
					<h2>Why get a second email address?</h2>
					<p className="dm-section-lead">A free Diamta address works well alongside the email you already use.</p>
					<div className="dm-grid dm-grid-benefits">
						{benefits.map((b) => (
							<div className="dm-card dm-card-sm" key={b.title}>
								<h3>{b.title}</h3>
								<p>{b.text}</p>
							</div>
						))}
					</div>
				</section>

				{/* Email app setup */}
				<section id="setup" className="dm-container dm-setup-wrap">
					<div className="dm-setup">
						<div className="dm-setup-intro">
							<h2>Set up Outlook or your phone</h2>
							<p>Use these settings in any email app. Your username is your full email address.</p>
						</div>
						<div className="dm-setup-cards">
							<dl className="dm-setup-card">
								<dt>Incoming · IMAP</dt>
								<dd>Server: <b>mail.diamta.com</b></dd>
								<dd>Port: <b>993</b></dd>
								<dd>Security: <b>SSL/TLS</b></dd>
							</dl>
							<dl className="dm-setup-card">
								<dt>Outgoing · SMTP</dt>
								<dd>Server: <b>mail.diamta.com</b></dd>
								<dd>Port: <b>587</b></dd>
								<dd>Security: <b>STARTTLS</b></dd>
							</dl>
						</div>
					</div>
				</section>

				{/* Final call to action */}
				<section className="dm-cta">
					<div className="dm-container dm-cta-inner">
						<div>
							<h2>Get your free email address today</h2>
							<p>It takes about a minute.</p>
						</div>
						<Link to="/registration" className="dm-btn dm-btn-light dm-btn-lg">Create a free account</Link>
					</div>
				</section>
			</main>

			<SiteFooter />
		</div>
	);
};

export default Home;
